import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, X, Map as MapIcon, ChevronLeft, ChevronRight, Clock, Share2, Maximize2 } from 'lucide-react';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Location } from '../types';
import { Map } from './Map';
import { getCachedRoute } from '../utils/routes';
import { getImageUrl } from '../utils/images';

interface LocationDetailsProps {
  location: Location;
  onClose: () => void;
  userLocation?: UserLocation | null;
}

export const LocationDetails: React.FC<LocationDetailsProps> = ({ location, onClose, userLocation }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [distance, setDistance] = useState<number | null>(null);
  const [driveTime, setDriveTime] = useState<number | null>(null);
  const [showShareTooltip, setShowShareTooltip] = useState(false);

  const images = location.images || [];
  const hasImages = images.length > 0;

  useEffect(() => {
    if (userLocation && location.coordinates) {
      getCachedRoute(userLocation, location.coordinates)
        .then(({ distance, duration }) => {
          setDistance(distance);
          setDriveTime(duration);
        })
        .catch(console.error);
    }
  }, [userLocation, location.coordinates]);

  const handleYandexNavigate = () => {
    if (!location.coordinates) return;
    const url = `https://yandex.ru/maps/?rtext=~${location.coordinates.lat},${location.coordinates.lng}`;
    window.open(url, '_blank');
  };

  const handleGoogleNavigate = () => {
    if (!location.coordinates) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.coordinates.lat},${location.coordinates.lng}`;
    window.open(url, '_blank');
  };

  const handleShare = async () => {
    const shareUrl = `https://moikutok.by/l/${location.short_id}`;
    const shareText = `${location.name}\n${shareUrl}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: location.name,
          text: location.shortDescription,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareText);
        setShowShareTooltip(true);
        setTimeout(() => setShowShareTooltip(false), 2000);
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const formatDuration = (minutes: number) => {
    if (minutes > 60) {
      const hours = minutes / 60;
      const roundedHours = Math.round(hours * 10) / 10;
      return `${Number.isInteger(roundedHours) ? Math.floor(roundedHours) : roundedHours} г.`;
    }
    return `${minutes} хв`;
  };

  const renderCoordinates = () => {
    if (!location.coordinates?.lat || !location.coordinates?.lng) return null;
    
    return (
      <div className="flex items-center gap-2 text-primary">
        <MapPin size={20} className="flex-shrink-0" />
        <span className="font-medium text-sm break-all">
          {location.coordinates.lat.toFixed(6)}, {location.coordinates.lng.toFixed(6)}
        </span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full h-full md:max-w-6xl md:h-[90vh] md:rounded-2xl relative flex flex-col md:flex-row overflow-hidden">
        <div className="absolute right-4 top-4 z-50 flex gap-2">
          <button
            onClick={handleShare}
            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors relative"
          >
            <Share2 size={24} />
            {showShareTooltip && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg whitespace-nowrap">
                Спасылка скапіравана
              </div>
            )}
          </button>
          <button
            onClick={onClose}
            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden flex flex-col h-full overflow-hidden">
          {/* Gallery */}
          {hasImages ? (
            <div className="relative aspect-[4/3] flex-shrink-0 bg-gray-100">
              <img
                src={getImageUrl(images[currentImageIndex])}
                alt={`${location.name} - фота ${currentImageIndex + 1}`}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setLightboxOpen(true)}
              />
              <div className="absolute inset-0 flex items-center justify-between px-4">
                <button
                  onClick={handlePrevImage}
                  className="p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                >
                  <ChevronLeft size={20} className="text-gray-700" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                >
                  <ChevronRight size={20} className="text-gray-700" />
                </button>
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1.5 rounded-full text-white text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            </div>
          ) : (
            <div className="aspect-[4/3] flex-shrink-0 bg-gray-100 flex items-center justify-center">
              <p className="text-gray-500">Няма фотаздымкаў</p>
            </div>
          )}

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">{location.name}</h2>

              {renderCoordinates()}

              {distance !== null && driveTime !== null && (
                <div className="flex items-center gap-4 text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <Navigation size={18} className="flex-shrink-0" />
                    <span>{distance} км</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock size={18} className="flex-shrink-0" />
                    <span>{formatDuration(driveTime)}</span>
                  </div>
                </div>
              )}

              <p className="text-gray-600 leading-relaxed">{location.shortDescription}</p>
              <p className="text-gray-600 leading-relaxed">{location.description}</p>

              <div className="flex flex-wrap gap-2">
                {location.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Map */}
              {location.coordinates && (
                <div className="h-[300px] rounded-xl overflow-hidden">
                  <Map
                    center={[location.coordinates.lat, location.coordinates.lng]}
                    height="100%"
                  />
                </div>
              )}

              {/* Navigation Buttons */}
              {location.coordinates && (
                <div className="flex gap-2 pt-2 pb-4">
                  <button
                    onClick={handleYandexNavigate}
                    className="flex-1 min-w-0 bg-primary text-white h-12 px-3 rounded-xl font-medium flex items-center justify-center gap-1.5 hover:bg-primary/90 transition-colors"
                  >
                    <Navigation size={18} className="flex-shrink-0" />
                    <span className="truncate">Яндекс</span>
                  </button>
                  <button
                    onClick={handleGoogleNavigate}
                    className="flex-1 min-w-0 bg-primary text-white h-12 px-3 rounded-xl font-medium flex items-center justify-center gap-1.5 hover:bg-primary/90 transition-colors"
                  >
                    <MapIcon size={18} className="flex-shrink-0" />
                    <span className="truncate">Google</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex md:w-1/2 flex-col">
          {/* Gallery Section */}
          <div className="relative h-[300px] flex-shrink-0">
            {hasImages ? (
              <>
                <img
                  src={getImageUrl(images[currentImageIndex])}
                  alt={`${location.name} - фота ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-between px-4">
                  <button
                    onClick={handlePrevImage}
                    className="p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronLeft size={20} className="text-gray-700" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronRight size={20} className="text-gray-700" />
                  </button>
                </div>
                <button
                  onClick={() => setLightboxOpen(true)}
                  className="absolute bottom-4 right-4 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                  title="Адкрыць галерэю"
                >
                  <Maximize2 size={20} className="text-gray-700" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1.5 rounded-full text-white text-sm">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <p className="text-gray-500">Няма фотаздымкаў</p>
              </div>
            )}
          </div>

          {/* Map Section */}
          <div className="flex-1 p-4">
            {location.coordinates && (
              <div className="h-full rounded-xl overflow-hidden">
                <Map
                  center={[location.coordinates.lat, location.coordinates.lng]}
                  height="100%"
                />
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          {location.coordinates && (
            <div className="flex gap-3 p-4">
              <button
                onClick={handleYandexNavigate}
                className="flex-1 bg-primary text-white h-12 px-4 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
              >
                <Navigation size={20} className="flex-shrink-0" />
                <span>Яндекс Навігатар</span>
              </button>
              <button
                onClick={handleGoogleNavigate}
                className="flex-1 bg-primary text-white h-12 px-4 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
              >
                <MapIcon size={20} className="flex-shrink-0" />
                Google Maps
              </button>
            </div>
          )}
        </div>

        {/* Desktop Right Column */}
        <div className="hidden md:block md:w-1/2 flex-col p-6 border-l border-gray-200 overflow-y-auto">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">{location.name}</h2>

            {renderCoordinates()}

            {distance !== null && driveTime !== null && (
              <div className="flex items-center gap-4 text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Navigation size={18} className="flex-shrink-0" />
                  <span>{distance} км</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock size={18} className="flex-shrink-0" />
                  <span>{formatDuration(driveTime)}</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {location.tags?.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="space-y-4 pt-4">
              <p className="text-gray-600 leading-relaxed">{location.shortDescription}</p>
              <p className="text-gray-600 leading-relaxed">{location.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Gallery */}
      {hasImages && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={currentImageIndex}
          slides={images.map(src => ({ src: getImageUrl(src) }))}
        />
      )}
    </div>
  );
};